import { RefObject, useEffect } from 'react';
import { fistbumpApi } from 'apis/CompanyAPI/tracking/fistbumpApi';
import { ContentEvent, ContentSurface } from 'apis/CompanyAPI/tracking/types';
import { createContentEventQueue } from 'domain/Updates/shared/impressionQueue';
import useIsElementVisible from './useIsElementVisible';

const FLUSH_INTERVAL_MS = 5000;
// A post counts as seen when at least half of it has been visible for a full second
const VISIBLE_THRESHOLD = 0.5;
const VISIBLE_FOR_MS = 1000;

const queue = createContentEventQueue(events => fistbumpApi.trackContent(events));
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let listeningForPageHide = false;

function flush() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = undefined;
  queue.flush();
}

function enqueue(event: ContentEvent) {
  queue.add(event);
  if (!listeningForPageHide) {
    listeningForPageHide = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
  }
  if (queue.hasPending() && !flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
  }
}

/** Logs that the user opened the post. Sent right away, together with any impressions waiting to be sent. */
export function trackPostOpen(contentId: string, surface: ContentSurface) {
  queue.add({ contentId, type: 'Open', surface });
  flush();
}

/**
 * Logs an impression for `contentId` once the element in `ref` has been at least 50% visible for 1 second.
 * Pass an undefined `contentId` to skip logging, e.g. for the company's own admins.
 */
export default function usePostImpression(
  contentId: string | undefined,
  ref: RefObject<HTMLElement>,
  surface: ContentSurface,
) {
  const isVisible = useIsElementVisible(ref, VISIBLE_THRESHOLD, VISIBLE_FOR_MS);

  useEffect(() => {
    if (isVisible && contentId) enqueue({ contentId, type: 'Impression', surface });
  }, [isVisible, contentId, surface]);
}
